import React, { useState } from 'react';

// Mock data for settings
const mockSchoolSettings = {
  schoolName: 'SchoolLine Academy',
  address: '123 Education Street, Learning City, LC 12345',
  phone: '+1 (555) 123-4567',
  email: 'admin@schoolline.edu',
  website: 'www.schoolline.edu',
  establishedYear: '1995',
  principalName: 'Dr. Sarah Johnson',
  timezone: 'America/New_York',
  academicYearStart: '2024-09-01',
  academicYearEnd: '2025-06-30'
};

const mockUserSettings = {
  theme: 'light',
  language: 'en',
  notifications: {
    email: true,
    push: true,
    sms: false
  },
  dashboard: {
    showQuickStats: true,
    showRecentActivity: true,
    showUpcomingEvents: true
  }
};

const mockSystemSettings = {
  maintenanceMode: false,
  allowRegistration: true,
  requireEmailVerification: true,
  sessionTimeout: 30,
  maxLoginAttempts: 5,
  backupFrequency: 'daily',
  dataRetentionPeriod: 365
};

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('school');
  const [schoolSettings, setSchoolSettings] = useState(mockSchoolSettings);
  const [userSettings, setUserSettings] = useState(mockUserSettings);
  const [systemSettings, setSystemSettings] = useState(mockSystemSettings);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const tabs = [
    { id: 'school', label: 'School Info', icon: '🏫' },
    { id: 'user', label: 'User Preferences', icon: '👤' },
    { id: 'system', label: 'System', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'backup', label: 'Backup & Data', icon: '💾' }
  ];

  const handleSave = () => {
    setShowSaveModal(true);
    // Simulate save operation
    setTimeout(() => {
      setShowSaveModal(false);
    }, 2000);
  };

  const renderSchoolSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
          <input
            type="text"
            value={schoolSettings.schoolName}
            onChange={(e) => setSchoolSettings({...schoolSettings, schoolName: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Principal Name</label>
          <input
            type="text"
            value={schoolSettings.principalName}
            onChange={(e) => setSchoolSettings({...schoolSettings, principalName: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
          <textarea
            value={schoolSettings.address}
            onChange={(e) => setSchoolSettings({...schoolSettings, address: e.target.value})}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
          <input
            type="tel"
            value={schoolSettings.phone}
            onChange={(e) => setSchoolSettings({...schoolSettings, phone: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={schoolSettings.email}
            onChange={(e) => setSchoolSettings({...schoolSettings, email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
          <input
            type="url"
            value={schoolSettings.website}
            onChange={(e) => setSchoolSettings({...schoolSettings, website: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Established Year</label>
          <input
            type="number"
            value={schoolSettings.establishedYear}
            onChange={(e) => setSchoolSettings({...schoolSettings, establishedYear: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
          <select
            value={schoolSettings.timezone}
            onChange={(e) => setSchoolSettings({...schoolSettings, timezone: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year Start</label>
          <input
            type="date"
            value={schoolSettings.academicYearStart}
            onChange={(e) => setSchoolSettings({...schoolSettings, academicYearStart: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year End</label>
          <input
            type="date"
            value={schoolSettings.academicYearEnd}
            onChange={(e) => setSchoolSettings({...schoolSettings, academicYearEnd: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );

  const renderUserSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
          <select
            value={userSettings.theme}
            onChange={(e) => setUserSettings({...userSettings, theme: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
          <select
            value={userSettings.language}
            onChange={(e) => setUserSettings({...userSettings, language: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>
        </div>
      </div>
      
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Notifications</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Email Notifications</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                notifications: {...userSettings.notifications, email: !userSettings.notifications.email}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.notifications.email ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.notifications.email ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Push Notifications</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                notifications: {...userSettings.notifications, push: !userSettings.notifications.push}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.notifications.push ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.notifications.push ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">SMS Notifications</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                notifications: {...userSettings.notifications, sms: !userSettings.notifications.sms}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.notifications.sms ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.notifications.sms ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>
      
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Dashboard Preferences</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show Quick Stats</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                dashboard: {...userSettings.dashboard, showQuickStats: !userSettings.dashboard.showQuickStats}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.dashboard.showQuickStats ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.dashboard.showQuickStats ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show Recent Activity</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                dashboard: {...userSettings.dashboard, showRecentActivity: !userSettings.dashboard.showRecentActivity}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.dashboard.showRecentActivity ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.dashboard.showRecentActivity ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show Upcoming Events</span>
            <button
              onClick={() => setUserSettings({
                ...userSettings,
                dashboard: {...userSettings.dashboard, showUpcomingEvents: !userSettings.dashboard.showUpcomingEvents}
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                userSettings.dashboard.showUpcomingEvents ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                userSettings.dashboard.showUpcomingEvents ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
          <input
            type="number"
            value={systemSettings.sessionTimeout}
            onChange={(e) => setSystemSettings({...systemSettings, sessionTimeout: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
          <input
            type="number"
            value={systemSettings.maxLoginAttempts}
            onChange={(e) => setSystemSettings({...systemSettings, maxLoginAttempts: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
          <select
            value={systemSettings.backupFrequency}
            onChange={(e) => setSystemSettings({...systemSettings, backupFrequency: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Data Retention (days)</label>
          <input
            type="number"
            value={systemSettings.dataRetentionPeriod}
            onChange={(e) => setSystemSettings({...systemSettings, dataRetentionPeriod: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Maintenance Mode</span>
            <p className="text-xs text-gray-500">Enable to restrict access during maintenance</p>
          </div>
          <button
            onClick={() => setSystemSettings({...systemSettings, maintenanceMode: !systemSettings.maintenanceMode})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              systemSettings.maintenanceMode ? 'bg-red-600' : 'bg-gray-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              systemSettings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Allow Registration</span>
            <p className="text-xs text-gray-500">Allow new users to register</p>
          </div>
          <button
            onClick={() => setSystemSettings({...systemSettings, allowRegistration: !systemSettings.allowRegistration})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              systemSettings.allowRegistration ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              systemSettings.allowRegistration ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Require Email Verification</span>
            <p className="text-xs text-gray-500">Require email verification for new accounts</p>
          </div>
          <button
            onClick={() => setSystemSettings({...systemSettings, requireEmailVerification: !systemSettings.requireEmailVerification})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              systemSettings.requireEmailVerification ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              systemSettings.requireEmailVerification ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'school':
        return renderSchoolSettings();
      case 'user':
        return renderUserSettings();
      case 'system':
        return renderSystemSettings();
      case 'security':
        return (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Security Settings</h3>
            <p className="text-gray-500">Advanced security configuration will be implemented here.</p>
          </div>
        );
      case 'backup':
        return (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💾</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Backup & Data Management</h3>
            <p className="text-gray-500">Backup and data management tools will be implemented here.</p>
          </div>
        );
      default:
        return renderSchoolSettings();
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Configure system settings and preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {tabs.find(tab => tab.id === activeTab)?.label}
              </h2>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
              >
                <span>💾</span>
                Save Changes
              </button>
            </div>

            {renderContent()}
          </div>
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Settings Saved</h3>
              <p className="text-gray-500">Your settings have been saved successfully.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage; 